import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["accessLevel", "objectId", "objectTypeId", "property", "value", "getAdditionalPropertyValue", "getSavedValue", "getDraftValue", "timestamp", "autoManageHighlySensitiveValue"];
import { useMemo, memo } from 'react';
import { CRM_OBJECT_TAG, FILE_ID, FILE_ID_OR_URL } from 'reference-resolvers-lite/constants/ReferenceTypes';
import { PropertyContextProvider } from '../context/PropertyContext/PropertyContextProvider';
import { useGetAdditionalPropertyValue } from '../hooks/useGetAdditionalPropertyValue';
import { usePropertyAccessLevel } from '../hooks/usePropertyAccessLevel';
import { useProperty } from '../hooks/useProperty';
import { usePropertyValue } from '../hooks/usePropertyValue';
import { useObjectType } from '../hooks/useObjectType';
import { useObjectTypeId } from '../hooks/useObjectTypeId';
import FormattedPropertyCurrencyV2 from './FormattedPropertyCurrencyV2';
import FormattedPropertyPrice from './FormattedPropertyPrice';
import FormattedPropertyHtml from './FormattedPropertyHtml';
import FormattedPropertyKnownGuid from './FormattedPropertyKnownGuid';
import FormattedPropertyPhone from './FormattedPropertyPhone';
import { isCurrency, isMultiCurrencyPrice, isHtml, isGuidPropertyWithKnownGuid, isMultienum, isPhoneNumber, isPaymentProcessor, isInvoiceDueDate, isTermBillingPeriod, isCustomUrlProperty } from 'customer-data-objects/property/PropertyIdentifier';
import { isSettlementCurrencyProperty } from '../utils/identifiers';
import PropertyInputHidden from 'customer-data-properties/input/PropertyInputHidden';
import FormattedInvoiceDueDate from 'customer-data-properties/formatted/FormattedInvoiceDueDate';
import FormattedPaymentProcessor from '../../formatted/FormattedPaymentProcessor';
import FormattedSettlementCurrency from '../../formatted/FormattedSettlementCurrency';
import { EMPTY_PROPERTY_VALUE } from '../../constants/EmptyPropertyValue';
import { HIDDEN } from 'customer-data-objects/property/AccessLevel';
import { getDataAttributes } from '../../utils/getDataAttributes';
import { usePropertyComponentResolver } from '../hooks/usePropertyComponentResolver';
import ExternalOptionValue from '../../value/ExternalOptionValue';
import { useObjectId } from '../hooks/useObjectId';
import { MARKETING_REASON } from '../../resolvers/CustomResolverReferenceTypes';
import { stringifyReasonId } from '../../resolvers/custom/MarketingReasonReferenceResolver';
import FormattedCrmObjectTags from '../../formatted/FormattedCrmObjectTags';
import { CONTACT_MARKETABLE_REASON_TYPE } from '../constants/AdditionalPropertyNames';
import FormattedPropertyFiles from './FormattedPropertyFiles';
import useGetAdditionalPropertyValueProxy from '../hooks/useGetAdditionalPropertyValueProxy';
import { normalizeProperty } from '../../utils/normalizeProperty';
import { useIsHidingHighlySensitiveValue } from '../../dataSensitivity/useIsHidingHighlySensitiveValue';
import { FormattedHighlySensitive } from '../../formatted/FormattedHighlySensitive';
import { HighlySensitivePropertyContextProvider } from '../../dataSensitivity/HighlySensitivePropertyContextProvider';
import { usePatchedProperty } from '../hooks/usePatchedProperty';
import useComponentMountMetric from '../../metrics/useComponentMountMetric';
import { FormattedPropertyDisplayedValue } from './FormattedPropertyDisplayedValue';
import FormattedPropertyTermPeriod from './FormattedPropertyTermPeriod';
import { useIsCrmCommandHandlerAvailable } from '../utils/useIsCrmCommandHandlerAvailable';
import { useFrameworkScoreConfig } from '../hooks/useFrameworkScoreConfig';
import { FormattedFrameworkScore } from './FormattedFrameworkScore';
import { useGetIsUngated } from '../hooks/useGetIsUngated';
import { gate } from 'hub-http/gates';
import { useFormattedPropertyCanaryMetrics } from '../../metrics/useFormattedPropertyCanaryMetrics';
import { FormattedPropertyMultiEnum } from './FormattedPropertyMultiEnum';
import { useIsTieredPricingProperty } from '../hooks/useIsTieredPricingProperty';
import { FormattedPropertyTieredPrice } from '../../input/tieredPricing/FormattedPropertyTieredPrice';
import PropertyInputAIStatus from '../../input/PropertyInputAIStatus';
import { useAIProcessingStatusWithMetadata } from '../../ai/useAIProcessingStatusWithMetadata';
import shouldShowAIComponentForStatus from '../../ai/shouldShowAIComponentForStatus';
import { useNormalizeObjectTypeId } from '../hooks/useNormalizeObjectTypeId';
import ForamttedPropertyUrl from './FormattedPropertyUrl';
import { jsx as _jsx } from "react/jsx-runtime";
const FormattedPropertyV2Wrapped = props => {
  const {
    placeholder = EMPTY_PROPERTY_VALUE,
    currencyProps,
    dateFormat,
    dateTimeFormat,
    relativeDate,
    relativeDateTime
  } = props;
  const accessLevel = usePropertyAccessLevel();
  const property = useProperty();
  const propertyValue = usePropertyValue();
  const objectType = useObjectType();
  const objectTypeId = useObjectTypeId();
  const objectId = useObjectId();
  const resolver = usePropertyComponentResolver();
  const getAdditionalPropertyValue = useGetAdditionalPropertyValue();
  const isHidingHighlySensitiveValue = useIsHidingHighlySensitiveValue();
  const {
    status: aiProcessingStatus
  } = useAIProcessingStatusWithMetadata(objectTypeId, property.name, objectId);
  const getIsUngated = useGetIsUngated();
  const linkFrameworkScore = getIsUngated(gate('CRM:PropertiesFE:LinkFrameworkScore')) &&
  // For the time being we only want to link the framework score number property
  // the score history for the enumeration threshold property is not ready yet
  // See: https://git.hubteam.com/HubSpot/PropertiesFrontendTeam/issues/2076
  property.type === 'number';
  const isCrmCommandHandlerAvailable = useIsCrmCommandHandlerAvailable();
  const frameworkScoreConfig = useFrameworkScoreConfig({
    objectTypeId,
    propertyName: property.name,
    skip: !linkFrameworkScore || !isCrmCommandHandlerAvailable
  });
  const isTieredPricingProperty = useIsTieredPricingProperty({
    objectTypeId,
    property
  });
  const isCustomUrl = isCustomUrlProperty(property) && getIsUngated(gate('CRM:PropMgmt:UrlPropertyKind'));
  if (accessLevel === HIDDEN) {
    return /*#__PURE__*/_jsx(PropertyInputHidden
    // We don't get a formControlUse prop in the formatter component so we have to
    // specify that we want the default behavior here. Usually this is set
    // in the top level PropertyInput component but we're skipping over it
    // here. This ensures we get a permissions icon and tooltip inline
    // with the value
    , Object.assign({
      formControlUse: "default"
    }, getDataAttributes(props)));
  }
  if (isHidingHighlySensitiveValue) {
    return /*#__PURE__*/_jsx(FormattedHighlySensitive, Object.assign({}, getDataAttributes(props)));
  }
  if (shouldShowAIComponentForStatus(aiProcessingStatus)) {
    return /*#__PURE__*/_jsx(PropertyInputAIStatus, Object.assign({}, getDataAttributes(props)));
  }

  // IMPORTANT: New variants should always be added below the accessLevel and highly-senistivie value checks above

  if (isSettlementCurrencyProperty(property, objectTypeId)) {
    return /*#__PURE__*/_jsx(FormattedSettlementCurrency, Object.assign({
      placeholder: placeholder,
      currencyProps: currencyProps
    }, getDataAttributes(props)));
  }
  if (isTieredPricingProperty) {
    return /*#__PURE__*/_jsx(FormattedPropertyTieredPrice, Object.assign({}, getDataAttributes(props)));
  }
  if (isMultiCurrencyPrice(property)) {
    return /*#__PURE__*/_jsx(FormattedPropertyPrice, Object.assign({
      placeholder: placeholder,
      currencyProps: currencyProps
    }, getDataAttributes(props)));
  }
  if (isCurrency(property)) {
    return /*#__PURE__*/_jsx(FormattedPropertyCurrencyV2, Object.assign({
      placeholder: placeholder,
      currencyProps: currencyProps
    }, getDataAttributes(props)));
  }
  if (isTermBillingPeriod(property, objectTypeId)) {
    const recurringBillingFrequency = getAdditionalPropertyValue('recurringbillingfrequency', objectId, objectTypeId);
    return /*#__PURE__*/_jsx(FormattedPropertyTermPeriod, Object.assign({
      placeholder: placeholder,
      recurringBillingFrequency: recurringBillingFrequency
    }, getDataAttributes(props)));
  }
  if ((resolver === null || resolver === void 0 ? void 0 : resolver.referenceType) === FILE_ID_OR_URL || (resolver === null || resolver === void 0 ? void 0 : resolver.referenceType) === FILE_ID) {
    return /*#__PURE__*/_jsx(FormattedPropertyFiles, Object.assign({
      placeholder: placeholder,
      resolver: resolver
    }, getDataAttributes(props)));
  }
  if (isHtml(property)) {
    return /*#__PURE__*/_jsx(FormattedPropertyHtml, Object.assign({
      placeholder: placeholder
    }, getDataAttributes(props)));
  }
  if (isGuidPropertyWithKnownGuid(property, objectType, propertyValue)) {
    return /*#__PURE__*/_jsx(FormattedPropertyKnownGuid, Object.assign({
      placeholder: placeholder
    }, getDataAttributes(props)));
  }
  if (isPhoneNumber(property)) {
    return /*#__PURE__*/_jsx(FormattedPropertyPhone, Object.assign({
      placeholder: placeholder
    }, getDataAttributes(props)));
  }
  if (isPaymentProcessor(property, objectType)) {
    return /*#__PURE__*/_jsx(FormattedPaymentProcessor, {
      property: property,
      placeholder: placeholder,
      value: propertyValue,
      objectTypeId: objectTypeId
    });
  }
  if (isInvoiceDueDate(property, objectType)) {
    return /*#__PURE__*/_jsx(FormattedInvoiceDueDate, {
      property: property,
      value: propertyValue,
      dateFormat: dateFormat,
      dateTimeFormat: dateTimeFormat,
      relativeDate: relativeDate,
      relativeDateTime: relativeDateTime,
      objectTypeId: objectTypeId
    });
  }
  if (resolver && resolver.referenceType === CRM_OBJECT_TAG) {
    return /*#__PURE__*/_jsx(FormattedCrmObjectTags, {
      resolver: resolver,
      value: propertyValue
    });
  }
  if (resolver) {
    let resolvableValue = propertyValue;
    if (resolver.referenceType === MARKETING_REASON) {
      // Marketing reasons resolve using the reason type+id instead of the value
      const marketableReasonType = getAdditionalPropertyValue(CONTACT_MARKETABLE_REASON_TYPE, objectId, objectTypeId);
      resolvableValue = marketableReasonType && propertyValue ? stringifyReasonId({
        reasonType: marketableReasonType,
        reasonSourceObjectId: propertyValue
      }) : propertyValue;
    }
    return /*#__PURE__*/_jsx(ExternalOptionValue, {
      multi: isMultienum(property),
      placeholder: placeholder,
      resolver: resolver,
      value: resolvableValue
    });
  }
  if (frameworkScoreConfig) {
    return /*#__PURE__*/_jsx(FormattedFrameworkScore, {
      objectId: objectId,
      propertyValue: propertyValue,
      frameworkScoreConfig: frameworkScoreConfig,
      dataAttributes: getDataAttributes(props),
      placeholder: placeholder
    });
  }
  if (isMultienum(property)) {
    return /*#__PURE__*/_jsx(FormattedPropertyMultiEnum, {
      property: property,
      propertyValue: propertyValue,
      objectTypeId: objectTypeId,
      placeholder: placeholder,
      dataAttributes: getDataAttributes(props)
    });
  }
  if (isCustomUrl) {
    return /*#__PURE__*/_jsx(ForamttedPropertyUrl, Object.assign({}, getDataAttributes(props)));
  }
  return /*#__PURE__*/_jsx(FormattedPropertyDisplayedValue, {
    property: property,
    propertyValue: propertyValue,
    dateFormat: dateFormat,
    dateTimeFormat: dateTimeFormat,
    relativeDate: relativeDate,
    relativeDateTime: relativeDateTime,
    objectTypeId: objectTypeId,
    placeholder: placeholder,
    dataAttributes: getDataAttributes(props)
  });
};
const FormattedPropertyV2 = props => {
  const {
      accessLevel: accessLevelOverride,
      objectId,
      objectTypeId,
      property,
      value,
      // @ts-expect-error TS confused by union, its OK if this pulls undefined
      getAdditionalPropertyValue,
      // @ts-expect-error TS confused by union, its OK if this pulls undefined
      getSavedValue,
      // @ts-expect-error TS confused by union, its OK if this pulls undefined
      getDraftValue,
      timestamp,
      autoManageHighlySensitiveValue = true
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  useComponentMountMetric('FormattedPropertyV2');
  useFormattedPropertyCanaryMetrics(props);
  const normalizedObjectTypeId = useNormalizeObjectTypeId({
    objectTypeOrId: objectTypeId,
    componentName: 'FormattedPropertyV2'
  });
  const normalizedProperty = useMemo(() => normalizeProperty(property), [property]);
  const patchedProperty = usePatchedProperty({
    property: normalizedProperty,
    objectTypeId: normalizedObjectTypeId
  });
  const getAdditionalPropertyValueProxy = useGetAdditionalPropertyValueProxy({
    getAdditionalPropertyValue,
    getDraftValue,
    getSavedValue,
    objectTypeId: normalizedObjectTypeId,
    propertyName: property.name,
    componentName: 'formattedpropertyv2'
  });
  return /*#__PURE__*/_jsx(HighlySensitivePropertyContextProvider, {
    children: /*#__PURE__*/_jsx(PropertyContextProvider, {
      accessLevelOverride: accessLevelOverride,
      objectTypeId: normalizedObjectTypeId,
      property: patchedProperty,
      value: value,
      autoManageHighlySensitiveValue: autoManageHighlySensitiveValue,
      timestamp: timestamp,
      getAdditionalPropertyValue: getAdditionalPropertyValueProxy,
      objectId: objectId,
      children: /*#__PURE__*/_jsx(FormattedPropertyV2Wrapped, Object.assign({}, rest))
    })
  });
};
export default /*#__PURE__*/memo(FormattedPropertyV2);